import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Tab,
  Tabs,
} from '@mui/material';
import { useState } from 'react';
import { ALL_OPTION } from '@features/campaigns/components/RealTimeOptimization/DimensionTabs';
import { DataGrid } from '@mui/x-data-grid';
import { significanceSx } from '@components/shared/significance-sx';
import { apiSlugToDimensionName } from '@components/Dashboard/dashboardSlice/responseMappers/dimensionStats';
import { AIInsightsCard } from '@components/shared/AIInsightsCard';
import { RslReport } from '../../../api/schema';
import { useRslAIInsights } from '../../../hooks/useRslAIInsights';
import {
  buildSalesColumns,
  OptimizationIndex as SalesOptimizationIndex,
} from './sales-columns';
import {
  buildVisitColumns,
  OptimizationIndex as VisitOptimizationIndex,
} from './visit-columns';
import {
  DimensionChips,
  DimensionChipsProps,
} from '@features/campaigns/components/RealTimeOptimization/DimensionChips/DimensionChips';
import { buildDimensionCounts } from '@features/reports';

const STATISTICALLY_INSIGNIFICANT_CLASSNAME = 'statistically-insignificant';

type MetricsType = 'sales' | 'visits';

type ComparisonByDimensionCardProps = {
  report: RslReport;
  campaignName: string;
};

const isDimensionSlug = (
  value: string
): value is keyof typeof apiSlugToDimensionName => {
  return value in apiSlugToDimensionName;
};

export const ComparisonByDimensionCard = (
  props: ComparisonByDimensionCardProps
) => {
  const [activeMetrics, setActiveMetrics] = useState<MetricsType>('sales');
  const [activeTab, setActiveTab] = useState<string>(ALL_OPTION.value);
  const [visitOptimizationIndex, setVisitOptimizationIndex] =
    useState<VisitOptimizationIndex>('incrementalVisitsPerImpressionIndex');
  const [salesOptimizationIndex, setSalesOptimizationIndex] =
    useState<SalesOptimizationIndex>(
      'projectedIncrementalTransactionValuePerImpressionIndex'
    );

  const handleTabChange: DimensionChipsProps['onChange'] = newValue => {
    setActiveTab(newValue);
  };
  const dimensionCounts = buildDimensionCounts(
    props.report.dimensions.map(d => ({ dimensionName: d.dimensionName }))
  );
  const filteredDimensions = props.report.dimensions.filter(
    d => activeTab === ALL_OPTION.value || d.dimensionName === activeTab
  );

  const aiInsightsTitle = isDimensionSlug(activeTab)
    ? apiSlugToDimensionName[activeTab]
    : 'All Dimensions';

  const dimensionNameForContribution =
    activeTab === ALL_OPTION.value ? 'all' : activeTab;

  const { rslAiInsights: contributionAiInsights } = useRslAIInsights(
    props.report,
    'contribution-metrics',
    dimensionNameForContribution
  );
  const { visitInsights } = useRslAIInsights(
    props.report,
    'summary',
    dimensionNameForContribution
  );

  return (
    <Card>
      {props.report.hasVisitMetrics ? (
        <Box>
          <Tabs
            value={activeMetrics}
            onChange={(_, value: MetricsType) => setActiveMetrics(value)}
          >
            <Tab value="sales" label="Sales Metrics" />
            <Tab value="visits" label="Visit Metrics" />
          </Tabs>
          <Divider orientation="horizontal" />
        </Box>
      ) : null}
      <CardHeader title="Comparison by Dimension" />
      <CardContent>
        <Stack direction="column" gap="1rem">
          <AIInsightsCard
            testId="rsl-comparison-by-dimension-ai-insights"
            aiInsights={
              activeMetrics === 'sales' ? contributionAiInsights : visitInsights
            }
            title={aiInsightsTitle}
            highlights={null}
          />

          <DimensionChips
            dimensionCounts={dimensionCounts}
            allSubdimensionsCount={props.report.dimensions.length}
            activeTab={activeTab}
            onChange={handleTabChange}
          />

          <DataGrid
            disableRowSelectionOnClick
            rows={filteredDimensions}
            columns={
              activeMetrics === 'sales'
                ? buildSalesColumns({
                    optimizationIndex: salesOptimizationIndex,
                    setOptimizationIndex: setSalesOptimizationIndex,
                  })
                : buildVisitColumns({
                    optimizationIndex: visitOptimizationIndex,
                    setOptimizationIndex: setVisitOptimizationIndex,
                  })
            }
            pageSizeOptions={[5, 10, 25]}
            getRowClassName={params =>
              params.row.statisticallySignificant
                ? ''
                : STATISTICALLY_INSIGNIFICANT_CLASSNAME
            }
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 5,
                },
              },
            }}
            sx={{
              height: 'auto',
              padding: '1rem',
              ...significanceSx({
                insignificantClassname: STATISTICALLY_INSIGNIFICANT_CLASSNAME,
              }),
            }}
            data-testid="comparison-by-dimension-table"
          />
        </Stack>
      </CardContent>
    </Card>
  );
};
